import {useApolloClient, ApolloClient} from "@apollo/client";
import {HookClass} from "@dvrt/graphql-widgets";
import {MutableRefObject, useEffect, useRef, useState} from "react";
import {
    UpdateExtensionProfileDocument,
    UpdateExtensionProfileQuery,
    UpdateExtensionProfileQueryVariables,
} from "./queries.generated";
import {DomainType} from "@/api";

type Data = {
    profiles: UpdateExtensionProfileQuery["userProfiles"];
    extension: UpdateExtensionProfileQuery["users"]["items"][number];
};

const initialData: Data = {
    profiles: [],
    extension: null,
};

export default class DataSource extends HookClass {
    public data: MutableRefObject<Data>;
    public loaded: boolean;

    protected extensionId: number;
    protected client: ApolloClient<object>;
    protected setLoaded: (value: boolean) => void;

    useHooks(extensionId: number) {
        [this.loaded, this.setLoaded] = useState<boolean>(false);
        this.client = useApolloClient();
        this.extensionId = extensionId;
        this.data = useRef(initialData);
        useEffect(() => {
            this.fetch();
            return () => {
                this.data.current = initialData;
            };
        }, []);
    }

    get activeProfileId(): number {
        if (!this.data.current.profiles.length) return 0;
        const activeProfiles = this.data.current.profiles.filter((p) => p.active);
        return activeProfiles.length ? activeProfiles[0].id : this.data.current.profiles[0].id;
    }

    get isResidential() {
        return this.data.current?.extension.number.domain.domainType === DomainType.Residential;
    }

    getProfile(id: number): UpdateExtensionProfileQuery["userProfiles"][number] {
        const profs = this.data.current.profiles.filter((p) => p.id === id);
        return profs.length ? profs[0] : null;
    }

    async fetch() {
        this.setLoaded(false);

        const result = await this.client.query<UpdateExtensionProfileQuery, UpdateExtensionProfileQueryVariables>({
            query: UpdateExtensionProfileDocument,
            variables: {
                extensionId: this.extensionId,
            },
            fetchPolicy: "no-cache",
        });

        this.data.current = {
            profiles: result.data.userProfiles.sort((a, b) => a.id - b.id),
            extension: result.data.users.items[0],
        };

        for (const profile of this.data.current.profiles)
            profile.rules = profile.rules.sort((a, b) => a.priority - b.priority);

        this.setLoaded(true);
    }
}
